import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ModernStoreProfileForm = ({ onProfileUpdate }) => {
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    ville: '',
    code_postal: '',
    cabis: '',
    telephone: '',
    email_professionnel: '',
    description: '',
    specialites: [],
    services_proposes: [],
    latitude: null,
    longitude: null
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  
  const specialitesOptions = [
    'Optométrie', 'Contactologie', 'Basse vision', 'Optique pédiatrique',
    'Réfraction', 'Adaptation lentilles', 'Lunetterie', 'Solaire',
    'Optique sportive', 'Vision professionnelle'
  ];

  const servicesOptions = [
    'Montage complexe', 'Verres progressifs', 'Montures percées',
    'Personnalisation avancée', 'Contactologie spécialisée', 
    'Réparations express', 'Service à domicile', 'Urgences'
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/magasins/profile`);
      setFormData(response.data);
      setIsEdit(true);
    } catch (error) {
      if (error.response?.status !== 404) {
        setError('Erreur lors du chargement du profil');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = isEdit ? 'PUT' : 'POST';
      const response = await axios({
        method: endpoint.toLowerCase(),
        url: `${API}/magasins/profile`,
        data: formData
      });
      
      onProfileUpdate && onProfileUpdate(response.data);
      alert(isEdit ? 'Profil mis à jour avec succès !' : 'Profil créé avec succès !');
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayToggle = (item, arrayName) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].includes(item)
        ? prev[arrayName].filter(s => s !== item)
        : [...prev[arrayName], item]
    }));
  };

  return (
    <div className="space-y-8">
      <h3 className="text-2xl font-bold text-white flex items-center">
        <span className="mr-3">🏢</span>
        {isEdit ? 'Mon Profil Magasin' : 'Créer mon Profil Magasin'}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Informations générales */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 shadow-lg">
          <h4 className="text-lg font-semibold text-white mb-6 flex items-center">
            <span className="mr-2">📋</span>
            Informations générales
          </h4>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Nom du magasin *
              </label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Numéro CABIS *
              </label>
              <input
                type="text"
                name="cabis"
                value={formData.cabis}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Adresse complète *
            </label>
            <input
              type="text"
              name="adresse"
              value={formData.adresse}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Ville *
              </label>
              <input
                type="text"
                name="ville"
                value={formData.ville}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Code postal *
              </label>
              <input
                type="text"
                name="code_postal"
                value={formData.code_postal}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Téléphone *
              </label>
              <input
                type="tel"
                name="telephone"
                value={formData.telephone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Email professionnel
              </label>
              <input
                type="email"
                name="email_professionnel"
                value={formData.email_professionnel}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 shadow-lg">
          <h4 className="text-lg font-semibold text-white mb-6 flex items-center">
            <span className="mr-2">📝</span>
            Présentation
          </h4>
          
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Description du magasin
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
              placeholder="Présentez votre magasin, votre équipe, votre philosophie, vos valeurs..."
            />
          </div>
        </div>

        {/* Spécialités */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 shadow-lg">
          <h4 className="text-lg font-semibold text-white mb-6 flex items-center">
            <span className="mr-2">🎯</span>
            Spécialités et Services
          </h4>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-200 mb-3">
              Spécialités du magasin
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {specialitesOptions.map((specialite) => (
                <label key={specialite} className="flex items-center space-x-2 cursor-pointer p-3 bg-slate-600/30 rounded-lg hover:bg-slate-600/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.specialites.includes(specialite)}
                    onChange={() => handleArrayToggle(specialite, 'specialites')}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-200">{specialite}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-3">
              Services proposés
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {servicesOptions.map((service) => (
                <label key={service} className="flex items-center space-x-2 cursor-pointer p-3 bg-slate-600/30 rounded-lg hover:bg-slate-600/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.services_proposes.includes(service)}
                    onChange={() => handleArrayToggle(service, 'services_proposes')}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-200">{service}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-600/20 border border-red-500/30 text-red-200 rounded-lg">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-medium py-4 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 shadow-lg"
        >
          {loading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Enregistrement...</span>
            </div>
          ) : (
            isEdit ? 'Mettre à jour mon profil' : 'Créer mon profil'
          )}
        </button>
      </form>
    </div>
  );
};

export const ModernOpticianProfileForm = ({ onProfileUpdate }) => {
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    telephone: '',
    ville: '',
    code_postal: '',
    experience_annees: 0,
    diplomes: [],
    specialites: [],
    competences_techniques: [],
    langues: ['Français'],
    mobilite_km: 50,
    description: '',
    salaire_souhaite_min: '',
    salaire_souhaite_max: '',
    vehicule: false,
    permis: false,
    disponible: true
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  
  const diplomesOptions = [
    'BTS Opticien-Lunetier', 'Licence Professionnelle Optique', 
    'Master Optique', 'Certificat Contactologie', 'Certificat Basse Vision',
    'Formation continue Optométrie', 'Diplôme européen d\'optométrie',
    'Formation montage complexe', 'Certification qualité'
  ];
  
  const specialitesOptions = [
    'Optométrie', 'Contactologie', 'Basse vision', 'Optique pédiatrique',
    'Réfraction', 'Adaptation lentilles', 'Lunetterie', 'Solaire',
    'Optique sportive', 'Vision professionnelle'
  ];

  const competencesOptions = [
    'Montage complexe', 'Verres progressifs', 'Montures percées',
    'Personnalisation avancée', 'Réparations spécialisées',
    'Vente conseil', 'Formation client', 'Gestion stock',
    'Optométrie avancée', 'Contactologie spécialisée'
  ];

  const languesOptions = [
    'Français', 'Anglais', 'Espagnol', 'Italien', 'Allemand',
    'Portugais', 'Arabe', 'Chinois', 'Russe'
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/opticiens/profile`);
      setFormData(response.data);
      setIsEdit(true);
    } catch (error) {
      if (error.response?.status !== 404) {
        setError('Erreur lors du chargement du profil');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = isEdit ? 'PUT' : 'POST';
      const response = await axios({
        method: endpoint.toLowerCase(),
        url: `${API}/opticiens/profile`,
        data: {
          ...formData,
          salaire_souhaite_min: formData.salaire_souhaite_min ? parseInt(formData.salaire_souhaite_min) : null,
          salaire_souhaite_max: formData.salaire_souhaite_max ? parseInt(formData.salaire_souhaite_max) : null,
        }
      });
      
      onProfileUpdate && onProfileUpdate(response.data);
      alert(isEdit ? 'Profil mis à jour avec succès !' : 'Profil créé avec succès !');
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleArrayToggle = (item, arrayName) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].includes(item)
        ? prev[arrayName].filter(s => s !== item)
        : [...prev[arrayName], item]
    }));
  };

  return (
    <div className="space-y-8">
      <h3 className="text-2xl font-bold text-white flex items-center">
        <span className="mr-3">👤</span>
        {isEdit ? 'Mon Profil Professionnel' : 'Créer mon Profil Professionnel'}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Informations personnelles */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 shadow-lg">
          <h4 className="text-lg font-semibold text-white mb-6 flex items-center">
            <span className="mr-2">👤</span>
            Informations personnelles
          </h4>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Prénom *
              </label>
              <input
                type="text"
                name="prenom"
                value={formData.prenom}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Nom *
              </label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Téléphone *
              </label>
              <input
                type="tel"
                name="telephone"
                value={formData.telephone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Ville *
              </label>
              <input
                type="text"
                name="ville"
                value={formData.ville}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Code postal *
              </label>
              <input
                type="text"
                name="code_postal"
                value={formData.code_postal}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
        </div>

        {/* Expérience et mobilité */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 shadow-lg">
          <h4 className="text-lg font-semibold text-white mb-6 flex items-center">
            <span className="mr-2">💼</span>
            Expérience et mobilité
          </h4>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Années d'expérience *
              </label>
              <input
                type="number"
                name="experience_annees"
                value={formData.experience_annees}
                onChange={handleInputChange}
                min="0"
                max="50"
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Rayon de mobilité (km)
              </label>
              <input
                type="number"
                name="mobilite_km"
                value={formData.mobilite_km}
                onChange={handleInputChange}
                min="0"
                max="200"
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <label className="flex items-center space-x-3 cursor-pointer p-4 bg-slate-600/30 rounded-lg hover:bg-slate-600/50 transition-colors">
              <input
                type="checkbox"
                name="permis"
                checked={formData.permis}
                onChange={handleInputChange}
                className="rounded text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-slate-200 font-medium">🚗 Permis de conduire</span>
                <p className="text-slate-400 text-sm">Je possède le permis B</p>
              </div>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer p-4 bg-slate-600/30 rounded-lg hover:bg-slate-600/50 transition-colors">
              <input
                type="checkbox"
                name="vehicule"
                checked={formData.vehicule}
                onChange={handleInputChange}
                className="rounded text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-slate-200 font-medium">🚙 Véhicule personnel</span>
                <p className="text-slate-400 text-sm">J'ai un véhicule à disposition</p>
              </div>
            </label>
          </div>
        </div>

        {/* Compétences */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 shadow-lg">
          <h4 className="text-lg font-semibold text-white mb-6 flex items-center">
            <span className="mr-2">🎯</span>
            Diplômes et Compétences
          </h4>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-200 mb-3">
              Diplômes et formations
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {diplomesOptions.map((diplome) => (
                <label key={diplome} className="flex items-center space-x-2 cursor-pointer p-3 bg-slate-600/30 rounded-lg hover:bg-slate-600/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.diplomes.includes(diplome)}
                    onChange={() => handleArrayToggle(diplome, 'diplomes')}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-200">{diplome}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-200 mb-3">
              Spécialités
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {specialitesOptions.map((specialite) => (
                <label key={specialite} className="flex items-center space-x-2 cursor-pointer p-3 bg-slate-600/30 rounded-lg hover:bg-slate-600/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.specialites.includes(specialite)}
                    onChange={() => handleArrayToggle(specialite, 'specialites')}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-200">{specialite}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-200 mb-3">
              Compétences techniques
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {competencesOptions.map((competence) => (
                <label key={competence} className="flex items-center space-x-2 cursor-pointer p-3 bg-slate-600/30 rounded-lg hover:bg-slate-600/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.competences_techniques.includes(competence)}
                    onChange={() => handleArrayToggle(competence, 'competences_techniques')}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-200">{competence}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-3">
              Langues parlées
            </label>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
              {languesOptions.map((langue) => (
                <label key={langue} className="flex items-center space-x-2 cursor-pointer p-3 bg-slate-600/30 rounded-lg hover:bg-slate-600/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.langues.includes(langue)}
                    onChange={() => handleArrayToggle(langue, 'langues')}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-200">{langue}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Présentation et rémunération */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 shadow-lg">
          <h4 className="text-lg font-semibold text-white mb-6 flex items-center">
            <span className="mr-2">💰</span>
            Présentation et rémunération
          </h4>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Présentation personnelle
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
              placeholder="Présentez votre expérience, vos motivations, vos objectifs professionnels..."
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Salaire souhaité min (€/mois)
              </label>
              <input
                type="number"
                name="salaire_souhaite_min"
                value={formData.salaire_souhaite_min}
                onChange={handleInputChange}
                min="0"
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Salaire souhaité max (€/mois)
              </label>
              <input
                type="number"
                name="salaire_souhaite_max"
                value={formData.salaire_souhaite_max}
                onChange={handleInputChange}
                min="0"
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <label className="flex items-center space-x-3 cursor-pointer p-4 bg-emerald-600/20 border border-emerald-500/30 rounded-lg">
            <input
              type="checkbox"
              name="disponible"
              checked={formData.disponible}
              onChange={handleInputChange}
              className="rounded text-emerald-600 focus:ring-emerald-500"
            />
            <div>
              <span className="text-emerald-200 font-medium">✅ Actuellement disponible</span>
              <p className="text-emerald-300 text-sm">Je suis disponible pour de nouvelles missions</p>
            </div>
          </label>
        </div>

        {error && (
          <div className="p-4 bg-red-600/20 border border-red-500/30 text-red-200 rounded-lg">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-medium py-4 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 shadow-lg"
        >
          {loading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Enregistrement...</span>
            </div>
          ) : (
            isEdit ? 'Mettre à jour mon profil' : 'Créer mon profil'
          )}
        </button>
      </form>
    </div>
  );
};